import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ErrorMessage from 'components/application/ErrorMessage';
import SidePanel from 'components/application/SidePanel';

import SelectedMembersList from 'components/bookings/SelectedMembersList';
import { useCurrentActor } from 'components/contexts/CurrentActor';
import FormFooter from 'components/sidepanels/FormFooter';
import AttachmentsField from 'components/training/attachments/components/AttachmentsField';
import TrainingFormFields from 'components/training/TrainingFormFields';

import { sendAnalytics } from 'components/helpers/analytics';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function BookingConnectionTrainingSidePanel({
  currentBookingConnectionTraining,
  domainCourse,
  domainPersonnelCollection,
  onBookingGroupMemberDelete,
  onCalendarClose,
  onCancel,
  onDateChange,
  onInputChange,
  onSubmit,
  removeErrorStyling,
  requestError,
  sidePanelContext,
  sidePanelIsOpen,
  submitDisabled,
}) {
  const { user: currentUser } = useCurrentActor();
  const [scrolledAmount, setScrolledAmount] = useState(0);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'bookings.BookingConnectionTrainingSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  return (
    <SidePanel
      bodyContent={
        <>
          <div className='popup__body-form--light'>
            <div className='tw-mb-6'>
              <ErrorMessage
                isFallback={requestError.isFallback}
                modifiers='side-panel__alert side-panel__alert--danger side-panel__alert--wide'
                validationErrors={requestError.validationErrors}
              />
            </div>
            <TrainingFormFields
              currentTraining={currentBookingConnectionTraining}
              domainCourse={domainCourse}
              notesExplanatoryMessage={i18nT('notesExplanatoryMessage')}
              onInputChange={onInputChange}
              onTrainingCalendarClose={onCalendarClose}
              onTrainingDateChange={onDateChange}
              removeErrorStyling={removeErrorStyling}
              requestError={requestError}
              submitDisabled={submitDisabled}
            />
          </div>
          <hr className='tw-my-0 tw-h-px tw-border-0 tw-bg-grey-100' />
          <div className='popup__body-form--light'>
            <div className='tw-mb-2 tw-flex tw-gap-2'>
              <div className='personal-info__label tw-font-medium tw-text-grey-900'>
                {i18nTCommon('shared.personnel')}
              </div>
              <div className='tw-flex tw-h-5 tw-items-center tw-justify-center tw-rounded-lg tw-bg-grey-050 tw-px-1.5 tw-text-center'>
                <p className='tw-mb-0 tw-font-inter tw-text-xs tw-font-semibold'>
                  {currentBookingConnectionTraining.personnelIds.length}
                </p>
              </div>
            </div>
            <SelectedMembersList
              domainPersonnelCollection={domainPersonnelCollection}
              onBookingGroupMemberDelete={onBookingGroupMemberDelete}
              selectedMemberIds={currentBookingConnectionTraining.personnelIds}
              shouldBlockFullRemoval={true}
            />
            <AttachmentsField
              isVisible={sidePanelIsOpen}
              label={i18nTCommon('shared.uploadEvidence')}
              maxUploads={25}
              name='attachmentIds'
              onChange={onInputChange}
              onUploadSuccess={() =>
                sendAnalytics('Training evidence uploaded', {
                  currentUser,
                  personnelCount:
                    currentBookingConnectionTraining.personnelIds.length,
                })
              }
              tooltip={i18nTCommon('shared.egCertificatesOrCards')}
              value={currentBookingConnectionTraining.attachmentIds}
              valueFormat='hashid'
            />
          </div>
        </>
      }
      closeCallback={onCancel}
      color='tertiary'
      contentContext={sidePanelContext}
      footerContent={
        <FormFooter
          onCancel={onCancel}
          onSubmit={onSubmit}
          submitButtonDisabled={submitDisabled}
          text={i18nTCommon('training.recordTraining')}
        />
      }
      headerContent={
        <div className='popup__title popup__title--tertiary-split'>
          <h1 className='tw-mb-2 tw-text-s tw-font-medium tw-tracking-tight'>
            {i18nTCommon('training.recordNewTraining')}
          </h1>
          <h2 className='truncated-text-container--wrapped truncated-text-container--five-lines tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
            {domainCourse.attributes.name}
          </h2>
        </div>
      }
      isOpen={sidePanelIsOpen}
      onBackdropClick={onCancel}
      scrolledAmount={scrolledAmount}
      setScrolledAmount={setScrolledAmount}
      submitDisabled={submitDisabled}
    />
  );
}

BookingConnectionTrainingSidePanel.propTypes = {
  currentBookingConnectionTraining: PropTypes.shape({
    expiryDate: PropTypes.instanceOf(Date),
    notes: PropTypes.string,
    personnelIds: PropTypes.array,
    startDate: PropTypes.instanceOf(Date),
    attachmentIds: PropTypes.array,
  }).isRequired,
  domainPersonnelCollection: PropTypes.arrayOf(resourceShape('personnel')),
  domainCourse: resourceShape('course').isRequired,
  onBookingGroupMemberDelete: PropTypes.func.isRequired,
  onCalendarClose: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  removeErrorStyling: PropTypes.func,
  requestError: PropTypes.object,
  sidePanelContext: PropTypes.string,
  sidePanelIsOpen: PropTypes.bool,
  submitDisabled: PropTypes.bool,
};
